import assert from 'assert';
import React from 'react';

import DataFrameColumnRelationshipMultiSelector from 'data-frames/column-relationship-selector/DataFrameColumnRelationshipMultiSelector';
import {DataFrameColumnRelationship} from 'data-frames/column-relationship-selector/DataFrameColumnRelationshipSelector';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {JoinType} from 'pipelines/joins/JoinConstants';
import JoinTypeSelector from 'pipelines/joins/JoinTypeSelector';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import getTestProps from 'test/lib/cypress/getTestProps';

import {getDataFrameInfoOrEmptyInfo, useStepConfigEditorContext} from '../../StepConfigEditorContext';
import {CustomComponentEditorProps} from '../../StepConfigEditorTypes';
import {VariableHeaderOrNull} from '../StepConfigFieldsTemplate';

const JOIN_TYPES_NOT_REQUIRING_CONDITIONS: Set<JoinType> = new Set(['NATURAL', 'POSITIONAL', 'CROSS']);

/**
 * Custom editor for JOIN steps. This component shares components with <CombineDataFramesView>.
 */
export default function JoinEditor({
  config,
  onChangeConfig,
  disabled,
  configShape,
}: CustomComponentEditorProps): JSX.Element {
  assert(configShape.kind === 'shape');
  assert(configShape.fields.joinConditions?.kind === 'array');
  assert(Array.isArray(config.joinConditions ?? []));

  const {dataFramesInfos} = useStepConfigEditorContext();

  const relationships = (
    (config.joinConditions as PipelineStepConfig<PipelineStepType.JOIN, 0, 'draft'>['joinConditions']) ?? []
  ).map(({leftDataSourceColumn, rightDataSourceColumn}) => ({
    leftColumn: leftDataSourceColumn,
    rightColumn: rightDataSourceColumn,
  }));

  return (
    <div {...getTestProps({component: 'JoinEditor', element: 'root'})}>
      <VariableHeaderOrNull variableName="Select a join type" isCompact={true} />
      <JoinTypeSelector
        joinType={config.joinType as JoinType}
        onSelectJoinType={(joinType) => {
          onChangeConfig(config, [], {
            ...config,
            joinType,
            joinConditions: JOIN_TYPES_NOT_REQUIRING_CONDITIONS.has(joinType) ? [] : config.joinConditions,
          });
        }}
        disabled={disabled}
      />
      {config.joinType && JOIN_TYPES_NOT_REQUIRING_CONDITIONS.has(config.joinType as JoinType) ? null : (
        <div className="u-small-spacing-top u-small-spacing-bottom">
          <VariableHeaderOrNull variableName="Specify join keys" isCompact={true} />
          <DataFrameColumnRelationshipMultiSelector
            leftDataFrame={getDataFrameInfoOrEmptyInfo(dataFramesInfos, 'Left').dataFrameSpec}
            rightDataFrame={getDataFrameInfoOrEmptyInfo(dataFramesInfos, 'Right').dataFrameSpec}
            relationships={relationships}
            onSelectRelationships={(newRelationships: Array<DataFrameColumnRelationship>): void => {
              onChangeConfig(
                config,
                ['joinConditions'],
                newRelationships.map(({leftColumn, rightColumn}) => ({
                  leftDataSourceColumn: leftColumn,
                  rightDataSourceColumn: rightColumn,
                }))
              );
            }}
            addItemLabel="Add join key"
            readOnly={disabled}
          />
        </div>
      )}
    </div>
  );
}
